/**
 * FluentSvgIcon.tsx
 * ----------------
 * SVG implementations of some commonly used Fluent/Fabric icons. Reinventing the wheel here saves us from having to
 * load the icon fonts asynchronously or having to include all icon definitions in our bundle.
 */

import * as React from 'react';
import { useId } from '@fluentui/react-hooks';

import { SvgProps } from '../../models/ui/svg';
import { isUndefinedOrWhiteSpace } from '../../utilities/strings';

interface FluentSvgIconProps extends SvgProps {
    iconName: string;
}

/**
 * Service icons
 */

export function FeedbackIcon(props: SvgProps) {
    const id = useId('feedback');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--feedback'
        : `fluent-svg-icon fluent-svg-icon--feedback ${className}`;

    if (height === undefined) {
        height = 15;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 15';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M1,4.2C1,4.5,1,4.7,1.1,5h-1C0,4.9,0,4.8,0,4.6c0-0.1,0-0.2,0-0.4c0-0.6,0.1-1.1,0.3-1.7c0.2-0.5,0.5-1,0.9-1.4
             c0.4-0.4,0.8-0.7,1.4-0.9C3.1,0.1,3.7,0,4.2,0c0.6,0,1.1,0.1,1.6,0.3c0.5,0.2,1,0.5,1.4,0.9L8,2l0.7-0.8c0.4-0.4,0.9-0.7,1.4-0.9
             C10.6,0.1,11.2,0,11.8,0c0.6,0,1.1,0.1,1.6,0.3c0.5,0.2,1,0.5,1.3,0.9c0.4,0.4,0.7,0.8,0.9,1.3C15.9,3.1,16,3.6,16,4.2
             c0,0.5-0.1,1-0.3,1.5C15.5,6.2,15.3,6.6,15,7h-3.7l-0.8-0.8L9.7,7H8.3l2.2-2.2L11.7,6h2.8c0.2-0.3,0.3-0.5,0.4-0.8
             C15,4.9,15,4.6,15,4.2c0-0.5-0.1-0.9-0.3-1.3c-0.2-0.4-0.4-0.7-0.7-1c-0.3-0.3-0.6-0.5-1-0.7C12.6,1.1,12.2,1,11.8,1
             c-0.4,0-0.8,0.1-1.2,0.2C10.1,1.4,9.8,1.7,9.5,2L8,3.4L6.5,2C6.2,1.6,5.9,1.4,5.5,1.2C5.1,1.1,4.7,1,4.2,1C3.8,1,3.4,1.1,3,1.3
             C2.6,1.4,2.2,1.7,2,2C1.7,2.2,1.4,2.6,1.3,3C1.1,3.4,1,3.8,1,4.2z M2.4,7l5.5,5.5c0,0.2,0.1,0.4,0.2,0.6c0.1,0.2,0.2,0.4,0.3,0.6
             L8,14L1.2,7.3C1.1,7.1,0.9,6.9,0.8,6.7C0.6,6.5,0.5,6.2,0.4,6h1.9l2.2-2.2L8,7.3v1.4L4.5,5.2L2.7,7H2.4z M13.7,8.2L13.7,8.2
             L13.7,8.2z M16,11.5c0,0.5-0.1,0.9-0.3,1.4c-0.2,0.4-0.4,0.8-0.8,1.1c-0.3,0.3-0.7,0.6-1.1,0.7C13.4,14.9,13,15,12.5,15
             c-0.5,0-0.9-0.1-1.4-0.3c-0.4-0.2-0.8-0.4-1.1-0.7c-0.3-0.3-0.6-0.7-0.8-1.1C9.1,12.4,9,12,9,11.5c0-0.5,0.1-0.9,0.3-1.4
             C9.5,9.7,9.7,9.3,10.1,9H9V8h3v3h-1V9.5c-0.3,0.2-0.6,0.5-0.7,0.9c-0.2,0.3-0.3,0.7-0.3,1.1c0,0.3,0.1,0.7,0.2,1
             c0.1,0.3,0.3,0.6,0.5,0.8c0.2,0.2,0.5,0.4,0.8,0.5c0.3,0.1,0.6,0.2,1,0.2c0.3,0,0.7-0.1,1-0.2s0.6-0.3,0.8-0.5
             c0.2-0.2,0.4-0.5,0.5-0.8c0.1-0.3,0.2-0.6,0.2-1c0-0.3,0-0.5-0.1-0.8c-0.1-0.2-0.2-0.5-0.3-0.7c-0.1-0.2-0.3-0.4-0.5-0.5
             c-0.2-0.2-0.4-0.3-0.7-0.4l0.3-0.9c0.3,0.1,0.7,0.3,0.9,0.5c0.3,0.2,0.5,0.5,0.7,0.8c0.2,0.3,0.4,0.6,0.5,0.9
             C15.9,10.8,16,11.1,16,11.5z"
                fill="currentColor"
            />
        </svg>
    );
}

export function VisualStudioCopilot(props: SvgProps) {
    const id = useId('copilotSvg');

    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--visualstudio-copilot'
        : `fluent-svg-icon fluent-svg-icon--visualstudio-copilot ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <g clipPath="url(#clip0_3995_17099)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.99814 15.0349C12.6492 15.0349 15.9999 12.0058 15.9999 11.2401V9.36753C15.9306 8.74371 15.3334 7.65848 14.4087 7.27319C14.3955 7.20305 14.3842 7.13054 14.3724 7.05497C14.3438 6.87219 14.3123 6.67138 14.2458 6.44254C14.447 5.93459 14.5 5.3595 14.5 4.78747C14.5 3.91675 14.3725 3.01816 13.8072 2.30264C13.2282 1.56982 12.313 1.17875 11.0828 1.04205C9.87699 0.908076 8.82113 1.07631 8.13918 1.80698C8.08969 1.86 8.03967 1.9152 7.99631 1.9716C7.95294 1.9152 7.90658 1.86 7.8571 1.80698C7.17514 1.07631 6.11928 0.908076 4.91348 1.04205C3.68325 1.17875 2.76803 1.56982 2.18906 2.30264C1.62376 3.01816 1.49631 3.91675 1.49631 4.78747C1.49631 5.3595 1.54926 5.93459 1.75044 6.44254C1.68393 6.67138 1.65252 6.87212 1.62392 7.05491C1.6121 7.1305 1.60075 7.20302 1.5876 7.27319C0.662905 7.65848 0.0657067 8.74371 -0.00366211 9.36753V11.2401C-0.00366211 12.0058 3.34708 15.0349 7.99814 15.0349ZM10.9172 2.53288C9.87297 2.41686 9.42883 2.62362 9.23578 2.83046C9.03194 3.04886 8.87725 3.50883 8.9942 4.44444C9.08492 5.17023 9.2965 5.67458 9.61188 5.99654C9.91096 6.30186 10.3963 6.53747 11.25 6.53747C12.1723 6.53747 12.5295 6.33869 12.6918 6.15788C12.8709 5.95823 13 5.57978 13 4.78747C13 4.02182 12.8774 3.54541 12.6302 3.23253C12.3967 2.93694 11.9369 2.64619 10.9172 2.53288ZM6.76184 2.83046C6.56879 2.62362 6.12465 2.41686 5.08045 2.53288C4.06068 2.64619 3.6009 2.93694 3.36737 3.23253C3.12018 3.54541 2.99763 4.02182 2.99763 4.78747C2.99763 5.57978 3.12668 5.95823 3.30583 6.15788C3.46808 6.33869 3.82531 6.53747 4.74763 6.53747C5.6013 6.53747 6.08666 6.30186 6.38574 5.99654C6.70112 5.67458 6.9127 5.17023 7.00342 4.44444C7.12037 3.50883 6.96568 3.04886 6.76184 2.83046ZM7.99816 13.5503C10.278 13.5503 12.5817 12.4403 13 12.1171V7.86173L12.9769 7.74622C12.4867 7.95586 11.9022 8.03747 11.25 8.03747C10.1037 8.03747 9.19051 7.70994 8.54033 7.04619C8.3213 6.82259 8.14351 6.5723 7.99999 6.30341C7.85647 6.5723 7.67503 6.82259 7.456 7.04619C6.80582 7.70994 5.89266 8.03747 4.74633 8.03747C4.0941 8.03747 3.50959 7.95586 3.01944 7.74622L2.99634 7.86173V12.1171C3.41459 12.4403 5.71829 13.5503 7.99816 13.5503Z"
                    fill="currentColor"
                />
                <path
                    d="M6.25 9.03747C6.66421 9.03747 7 9.37325 7 9.78747V11.2875C7 11.7017 6.66421 12.0375 6.25 12.0375C5.83579 12.0375 5.5 11.7017 5.5 11.2875V9.78747C5.5 9.37325 5.83579 9.03747 6.25 9.03747Z"
                    fill="currentColor"
                />
                <path
                    d="M10.5 9.78747C10.5 9.37325 10.1642 9.03747 9.75 9.03747C9.33579 9.03747 9 9.37325 9 9.78747V11.2875C9 11.7017 9.33579 12.0375 9.75 12.0375C10.1642 12.0375 10.5 11.7017 10.5 11.2875V9.78747Z"
                    fill="currentColor"
                />
            </g>
        </svg>
    );
}

export function GitHubCodespacesIcon(props: SvgProps) {
    const id = useId('codespacesSvg');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--visualstudio-codespaces'
        : `fluent-svg-icon fluent-svg-icon--visualstudio-codespaces ${className}`;

    if (height === undefined) {
        height = 10;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 10';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M12.9766 4C13.3932 4 13.7839 4.07812 14.1484 4.23438C14.5182 4.39062 14.8385 4.60417 15.1094 4.875C15.3854 5.14062 15.6016 5.45573 15.7578 5.82031C15.9193 6.1849 16 6.57812 16 7C16 7.41667 15.9219 7.80729 15.7656 8.17188C15.6094 8.53125 15.3932 8.84896 15.1172 9.125C14.8464 9.39583 14.5286 9.60938 14.1641 9.76562C13.7995 9.92188 13.4115 10 13 10H4C3.44792 10 2.92708 9.89583 2.4375 9.6875C1.95312 9.47917 1.52865 9.19531 1.16406 8.83594C0.804688 8.47135 0.520833 8.04688 0.3125 7.5625C0.104167 7.07292 0 6.55208 0 6C0 5.44792 0.104167 4.92969 0.3125 4.44531C0.520833 3.95573 0.804688 3.53125 1.16406 3.17188C1.52865 2.80729 1.95312 2.52083 2.4375 2.3125C2.92708 2.10417 3.44792 2 4 2C4.23958 2 4.48177 2.02344 4.72656 2.07031C4.9349 1.7474 5.17448 1.45833 5.44531 1.20312C5.72135 0.942708 6.02083 0.723958 6.34375 0.546875C6.66667 0.369792 7.01042 0.234375 7.375 0.140625C7.73958 0.046875 8.11458 0 8.5 0C9.07812 0 9.6224 0.101562 10.1328 0.304688C10.6484 0.507812 11.1042 0.789062 11.5 1.14844C11.8958 1.5026 12.2214 1.92448 12.4766 2.41406C12.737 2.90365 12.9036 3.43229 12.9766 4ZM13 9C13.276 9 13.5339 8.94792 13.7734 8.84375C14.0182 8.73958 14.2318 8.59635 14.4141 8.41406C14.5964 8.23177 14.7396 8.02083 14.8438 7.78125C14.9479 7.53646 15 7.27604 15 7C15 6.72396 14.9479 6.46615 14.8438 6.22656C14.7396 5.98177 14.5964 5.76823 14.4141 5.58594C14.2318 5.40365 14.0182 5.26042 13.7734 5.15625C13.5339 5.05208 13.276 5 13 5H12V4.5C12 4.01562 11.9089 3.5625 11.7266 3.14062C11.5443 2.71354 11.2943 2.34115 10.9766 2.02344C10.6589 1.70573 10.2865 1.45573 9.85938 1.27344C9.4375 1.09115 8.98438 1 8.5 1C8.13542 1 7.78385 1.05469 7.44531 1.16406C7.10677 1.27344 6.79427 1.42708 6.50781 1.625C6.22656 1.82292 5.97656 2.0625 5.75781 2.34375C5.53906 2.61979 5.36458 2.92969 5.23438 3.27344C4.84375 3.09115 4.43229 3 4 3C3.58333 3 3.19271 3.07812 2.82812 3.23438C2.46875 3.39062 2.15104 3.60677 1.875 3.88281C1.60417 4.15365 1.39062 4.47135 1.23438 4.83594C1.07812 5.20052 1 5.58854 1 6C1 6.41667 1.07812 6.80729 1.23438 7.17188C1.39062 7.53125 1.60417 7.84896 1.875 8.125C2.15104 8.39583 2.46875 8.60938 2.82812 8.76562C3.19271 8.92188 3.58333 9 4 9H13Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function IdentitySignInIcon(props: SvgProps) {
    const id = useId('identitySvg');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--identity'
        : `fluent-svg-icon fluent-svg-icon--identity ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M16,12.5l-2.9,2.9l-0.7-0.7L14,13H9v-1h5l-1.6-1.6l0.7-0.7L16,12.5z M10.6,11c-0.2-0.4-0.5-0.9-0.8-1.2
             C9.5,9.4,9.1,9.1,8.7,8.8C8.3,8.6,7.9,8.4,7.4,8.2C7,8.1,6.5,8,6,8C5.5,8,5.1,8.1,4.7,8.2C4.2,8.3,3.8,8.5,3.5,8.7
             c-0.4,0.2-0.7,0.5-1,0.8c-0.3,0.3-0.6,0.6-0.8,1c-0.2,0.4-0.4,0.8-0.5,1.2S1,12.5,1,13H0c0-0.6,0.1-1.2,0.3-1.8
             c0.2-0.6,0.5-1.1,0.8-1.6c0.3-0.5,0.8-0.9,1.2-1.3c0.5-0.4,1-0.7,1.6-0.9C3.6,7.2,3.3,7,3.1,6.7C2.9,6.5,2.7,6.2,2.5,5.9
             S2.2,5.3,2.1,5C2,4.7,2,4.3,2,4c0-0.6,0.1-1.1,0.3-1.6C2.5,2,2.8,1.5,3.2,1.2C3.5,0.8,4,0.5,4.4,0.3C4.9,0.1,5.4,0,6,0
             s1.1,0.1,1.6,0.3C8,0.5,8.5,0.8,8.8,1.2C9.2,1.5,9.5,2,9.7,2.4C9.9,2.9,10,3.4,10,4c0,0.7-0.2,1.4-0.5,1.9C9.2,6.5,8.7,7,8.1,7.4
             c0.4,0.1,0.7,0.3,1.1,0.5c0.3,0.2,0.6,0.4,0.9,0.7c0.3,0.3,0.5,0.6,0.8,0.9c0.2,0.3,0.4,0.7,0.6,1L10.6,11z M3,4
             c0,0.4,0.1,0.8,0.2,1.2c0.2,0.4,0.4,0.7,0.6,1c0.3,0.3,0.6,0.5,1,0.6C5.2,6.9,5.6,7,6,7c0.4,0,0.8-0.1,1.2-0.2
             c0.4-0.2,0.7-0.4,1-0.6c0.3-0.3,0.5-0.6,0.6-1C8.9,4.8,9,4.4,9,4c0-0.4-0.1-0.8-0.2-1.2c-0.2-0.4-0.4-0.7-0.6-1
             c-0.3-0.3-0.6-0.5-1-0.6C6.8,1.1,6.4,1,6,1C5.6,1,5.2,1.1,4.8,1.2c-0.4,0.2-0.7,0.4-1,0.6c-0.3,0.3-0.5,0.6-0.6,1C3.1,3.2,3,3.6,3,4
             z"
                fill="currentColor"
            />
        </svg>
    );
}

export function IntellicodeIcon(props: SvgProps) {
    const id = useId('intellicodeSvg');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--intellicode'
        : `fluent-svg-icon fluent-svg-icon--intellicode ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 11 16';
    }

    if (width === undefined) {
        width = 11;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M5.5 0C6.00521 0 6.49219 0.0651042 6.96094 0.195312C7.42969 0.325521 7.86719 0.510417 8.27344 0.75C8.6849 0.989583 9.05729 1.27604 9.39062 1.60938C9.72396 1.94271 10.0104 2.3151 10.25 2.72656C10.4896 3.13281 10.6745 3.57031 10.8047 4.03906C10.9349 4.50781 11 4.99479 11 5.5C11 6.25521 10.8568 6.95573 10.5703 7.60156C10.2839 8.2474 9.875 8.83333 9.34375 9.35938C8.91146 9.78646 8.57812 10.263 8.34375 10.7891C8.11458 11.3151 8 11.8854 8 12.5V14.5C8 14.7083 7.96094 14.9036 7.88281 15.0859C7.80469 15.2682 7.69792 15.4271 7.5625 15.5625C7.42708 15.6979 7.26823 15.8047 7.08594 15.8828C6.90365 15.9609 6.70833 16 6.5 16H4.5C4.29167 16 4.09635 15.9609 3.91406 15.8828C3.73177 15.8047 3.57292 15.6979 3.4375 15.5625C3.30208 15.4271 3.19531 15.2682 3.11719 15.0859C3.03906 14.9036 3 14.7083 3 14.5V12.5C3 11.8854 2.88281 11.3151 2.64844 10.7891C2.41927 10.263 2.08854 9.78646 1.65625 9.35938C1.125 8.83333 0.716146 8.2474 0.429688 7.60156C0.143229 6.95573 0 6.25521 0 5.5C0 4.99479 0.0651042 4.50781 0.195312 4.03906C0.325521 3.57031 0.510417 3.13281 0.75 2.72656C0.989583 2.3151 1.27604 1.94271 1.60938 1.60938C1.94271 1.27604 2.3125 0.989583 2.71875 0.75C3.13021 0.510417 3.57031 0.325521 4.03906 0.195312C4.50781 0.0651042 4.99479 0 5.5 0ZM6.5 15C6.63542 15 6.7526 14.9505 6.85156 14.8516C6.95052 14.7526 7 14.6354 7 14.5V13H4V14.5C4 14.6354 4.04948 14.7526 4.14844 14.8516C4.2474 14.9505 4.36458 15 4.5 15H6.5ZM7.02344 12C7.09115 11.3281 7.26302 10.7188 7.53906 10.1719C7.8151 9.625 8.1875 9.11458 8.65625 8.64062C9.08854 8.20312 9.41927 7.72656 9.64844 7.21094C9.88281 6.6901 10 6.11979 10 5.5C10 4.88021 9.88021 4.29688 9.64062 3.75C9.40625 3.20312 9.08594 2.72656 8.67969 2.32031C8.27344 1.91406 7.79688 1.59375 7.25 1.35938C6.70312 1.11979 6.11979 1 5.5 1C4.88021 1 4.29688 1.11979 3.75 1.35938C3.20312 1.59375 2.72656 1.91406 2.32031 2.32031C1.91406 2.72656 1.59115 3.20312 1.35156 3.75C1.11719 4.29688 1 4.88021 1 5.5C1 6.11979 1.11458 6.6901 1.34375 7.21094C1.57812 7.72656 1.91146 8.20312 2.34375 8.64062C2.8125 9.11458 3.1849 9.625 3.46094 10.1719C3.73698 10.7188 3.90885 11.3281 3.97656 12H7.02344Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function LicensingIcon(props: SvgProps) {
    const id = useId('licensingSvg');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--licensing'
        : `fluent-svg-icon fluent-svg-icon--licensing ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M10.5 11.7969L13.1484 9.14844L13.8516 9.85156L10.5 13.2031L8.64844 11.3516L9.35156 10.6484L10.5 11.7969ZM16 6V16H6V9.89844C5.68229 9.96615 5.34896 10 5 10C4.30729 10 3.65625 9.86979 3.04688 9.60938C2.44271 9.34896 1.91406 8.99219 1.46094 8.53906C1.00781 8.08594 0.651042 7.55729 0.390625 6.95312C0.130208 6.34375 0 5.69271 0 5C0 4.30729 0.130208 3.65885 0.390625 3.05469C0.651042 2.44531 1.00781 1.91406 1.46094 1.46094C1.91406 1.00781 2.44271 0.651042 3.04688 0.390625C3.65625 0.130208 4.30729 0 5 0C5.69271 0 6.34115 0.130208 6.94531 0.390625C7.55469 0.651042 8.08594 1.00781 8.53906 1.46094C8.99219 1.91406 9.34896 2.44531 9.60938 3.05469C9.86979 3.65885 10 4.30729 10 5C10 5.34896 9.96615 5.68229 9.89844 6H16ZM1 5C1 5.55208 1.10417 6.07031 1.3125 6.55469C1.52604 7.03906 1.8125 7.46354 2.17188 7.82812C2.53646 8.1875 2.96094 8.47396 3.44531 8.6875C3.92969 8.89583 4.44792 9 5 9C5.55208 9 6.07031 8.89583 6.55469 8.6875C7.03906 8.47396 7.46094 8.1875 7.82031 7.82812C8.1849 7.46354 8.47135 7.03906 8.67969 6.55469C8.89323 6.07031 9 5.55208 9 5C9 4.44792 8.89323 3.92969 8.67969 3.44531C8.47135 2.96094 8.1849 2.53906 7.82031 2.17969C7.46094 1.8151 7.03906 1.52865 6.55469 1.32031C6.07031 1.10677 5.55208 1 5 1C4.44792 1 3.92969 1.10677 3.44531 1.32031C2.96094 1.52865 2.53646 1.8151 2.17188 2.17969C1.8125 2.53906 1.52604 2.96094 1.3125 3.44531C1.10417 3.92969 1 4.44792 1 5ZM15 7H9.58594C9.32031 7.58854 8.96875 8.10156 8.53125 8.53906C8.09896 8.97135 7.58854 9.32031 7 9.58594V15H15V7ZM7.85156 3.85156L4.5 7.20312L2.64844 5.35156L3.35156 4.64844L4.5 5.79688L7.14844 3.14844L7.85156 3.85156Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function LiveShareIcon(props: SvgProps) {
    const id = useId('liveshare');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--liveshare'
        : `fluent-svg-icon fluent-svg-icon--liveshare ${className}`;

    if (height === undefined) {
        height = 14;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 14';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M11,12l1-1v3H0V4h1v9h10V12z M10,8C9.3,8,8.7,8.1,8,8.2C7.4,8.3,6.8,8.5,6.2,8.8C5.6,9,5,9.4,4.5,9.7C4,10.1,3.5,10.5,3,11
             v-1c0-0.6,0.1-1.3,0.2-1.9C3.4,7.5,3.7,7,4,6.5c0.3-0.5,0.7-1,1.1-1.4C5.5,4.6,5.9,4.3,6.5,4C7,3.7,7.5,3.4,8.1,3.2
             C8.7,3.1,9.4,3,10,3V0l5.5,5.5L10,11V8z M10.8,4c-0.3,0-0.6,0-0.8,0C9.7,4,9.5,4,9.2,4.1C9,4.1,8.7,4.1,8.5,4.2
             C8.2,4.3,7.9,4.4,7.7,4.5C7.2,4.7,6.8,4.9,6.4,5.2c-0.4,0.3-0.7,0.6-1,1C5.1,6.6,4.8,7,4.6,7.4C4.4,7.8,4.2,8.3,4.1,8.8
             c0.9-0.6,1.8-1,2.8-1.3C7.9,7.1,8.9,7,10,7h1v1.6l3.1-3.1L11,2.4V4H10.8z"
                fill="currentColor"
            />
        </svg>
    );
}

export function MarketplaceIcon(props: SvgProps) {
    const id = useId('marketplaceSvg');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--marketplace'
        : `fluent-svg-icon fluent-svg-icon--marketplace ${className}`;

    if (height === undefined) {
        height = 13;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 13 13';
    }

    if (width === undefined) {
        width = 13;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M13 3V13H0V3H2.28125C2.09896 2.69271 2.00781 2.35938 2.00781 2C2.00781 1.72396 2.0599 1.46615 2.16406 1.22656C2.26823 0.981771 2.40885 0.768229 2.58594 0.585938C2.76823 0.403646 2.97917 0.260417 3.21875 0.15625C3.46354 0.0520833 3.72396 0 4 0C4.50521 0 4.97656 0.119792 5.41406 0.359375C5.85677 0.59375 6.21875 0.921875 6.5 1.34375C6.78125 0.921875 7.14062 0.59375 7.57812 0.359375C8.02083 0.119792 8.49479 0 9 0C9.27604 0 9.53385 0.0520833 9.77344 0.15625C10.0182 0.260417 10.2318 0.403646 10.4141 0.585938C10.5964 0.768229 10.7396 0.981771 10.8438 1.22656C10.9479 1.46615 11 1.72396 11 2C11 2.17708 10.974 2.35156 10.9219 2.52344C10.875 2.6901 10.8073 2.84896 10.7188 3H13ZM6 4H1V12H6V4ZM6 3C6 2.72396 5.94792 2.46615 5.84375 2.22656C5.73958 1.98177 5.59635 1.76823 5.41406 1.58594C5.23177 1.40365 5.01823 1.26042 4.77344 1.15625C4.53385 1.05208 4.27604 1 4 1C3.85938 1 3.72917 1.02604 3.60938 1.07812C3.48958 1.13021 3.38281 1.20312 3.28906 1.29688C3.20052 1.38542 3.13021 1.48958 3.07812 1.60938C3.02604 1.72917 3 1.85938 3 2C3 2.14062 3.02604 2.27083 3.07812 2.39062C3.13021 2.51042 3.20052 2.61719 3.28906 2.71094C3.38281 2.79948 3.48958 2.86979 3.60938 2.92188C3.72917 2.97396 3.85938 3 4 3H6ZM9 1C8.72396 1 8.46354 1.05208 8.21875 1.15625C7.97917 1.26042 7.76823 1.40365 7.58594 1.58594C7.40365 1.76823 7.26042 1.98177 7.15625 2.22656C7.05208 2.46615 7 2.72396 7 3H9C9.14062 3 9.27083 2.97396 9.39062 2.92188C9.51042 2.86979 9.61458 2.79948 9.70312 2.71094C9.79688 2.61719 9.86979 2.51042 9.92188 2.39062C9.97396 2.27083 10 2.14062 10 2C10 1.85938 9.97396 1.72917 9.92188 1.60938C9.86979 1.48958 9.79688 1.38542 9.70312 1.29688C9.61458 1.20312 9.51042 1.13021 9.39062 1.07812C9.27083 1.02604 9.14062 1 9 1ZM12 4H7V12H12V4Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function RoamingIcon(props: SvgProps) {
    const id = useId('roamingSvg');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--roaming'
        : `fluent-svg-icon fluent-svg-icon--roaming ${className}`;

    if (height === undefined) {
        height = 14;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 13 14';
    }

    if (width === undefined) {
        width = 13;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <path
                d="M13 9H7V10H8V11H10V12H13V13H10V14H3V13H0V12H3V11H5V10H6V9H0V0H13V9ZM9 12H7V11H6V12H4V13H9V12ZM1 8H12V1H1V8Z"
                fill="currentColor"
            />
        </svg>
    );
}

/**
 * Other icons
 */

export function ChevronDownIcon(props: SvgProps) {
    const id = useId('chevron-down');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--chevron-down'
        : `fluent-svg-icon fluent-svg-icon--chevron-down ${className}`;

    if (height === undefined) {
        height = 10;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 17 10';
    }

    if (width === undefined) {
        width = 17;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M15.8335 0.648438L16.5366 1.35156L8.68505 9.20312L0.83349 1.35156L1.53662 0.648438L8.68505 7.79688L15.8335 0.648438Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ChevronDownMedIcon(props: SvgProps) {
    const id = useId('chevron-down-med');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--chevron-down-med'
        : `fluent-svg-icon fluent-svg-icon--chevron-down-med ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            className={iconClassName}
            height={height}
            id={id}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            width={width}
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M15.1484 0.648438L15.8516 1.35156L8 9.20312L0.148438 1.35156L0.851562 0.648438L8 7.79688L15.1484 0.648438Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ChevronRightMedIcon(props: SvgProps) {
    const id = useId('chevron-right-med');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--chevron-right-med'
        : `fluent-svg-icon fluent-svg-icon--chevron-right-med ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            className={iconClassName}
            height={height}
            id={id}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            width={width}
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M3.648438 0.851562L4.35156 0.148438L12.20312 8L4.35156 15.8516L3.648438 15.1484L10.79688 8L3.648438 0.851562Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ChevronUpMedIcon(props: SvgProps) {
    const id = useId('chevron-up-med');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--chevron-up-med'
        : `fluent-svg-icon fluent-svg-icon--chevron-up-med ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            className={iconClassName}
            height={height}
            id={id}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            width={width}
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M0.851562 12.35156L0.148438 11.64844L8 3.796875L15.8516 11.64844L15.1484 12.35156L8 5.20312L0.851562 12.35156Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function PageListFilterIcon(props: SvgProps) {
    const id = useId('page-list-filter');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--page-list-filter'
        : `fluent-svg-icon fluent-svg-icon--page-list-filter ${className}`;

    if (height === undefined) {
        height = 21;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 21 21';
    }

    if (width === undefined) {
        width = 21;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            className={iconClassName}
            height={height}
            id={id}
            version="1.1"
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            width={width}
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M11.8125 13.125H21V14.6836L18.375 17.6367V21H14.4375V17.6367L11.8125 14.6836V13.125ZM17.0625 17.1445L19.4619 14.4375H13.3506L15.75 17.1445V19.6875H17.0625V17.1445ZM13.125 3.9375V5.25H5.25V3.9375H13.125ZM5.25 13.125V11.8125H10.5V13.125H5.25ZM1.3125 17.0625H12.1714L13.125 18.1392V18.375H0V0H15.75V11.8125H14.4375V1.3125H1.3125V17.0625ZM13.125 7.875V9.1875H5.25V7.875H13.125ZM3.9375 3.9375V5.25H2.625V3.9375H3.9375ZM3.9375 7.875V9.1875H2.625V7.875H3.9375ZM2.625 13.125V11.8125H3.9375V13.125H2.625Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function VisualStudioSetupIcon(props: SvgProps) {
    const id = useId('visualstudio-setup');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--visualstudio-setup'
        : `fluent-svg-icon fluent-svg-icon--visualstudio-setup ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            version="1.1"
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            x={x}
            y={y}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
        >
            {title && <title>{title}</title>}
            <rect width={width} height={height} fill="none" />
            <path
                fillRule="evenodd"
                d="M15.94,2.88c-.04-.09-.1-.17-.18-.23L12.76,.15h-.01c-.01-.02-.03-.03-.05-.04-.11-.07-.24-.11-.37-.11-.09,0-.18,.02-.27,.06-.08,.03-.16,.08-.22,.15l-.01,.02L6.63,5.86,3.13,2.69l-.02-.02c-.12-.11-.28-.17-.44-.17-.14,0-.28,.05-.4,.13l-.04,.03L.27,4.13c-.09,.06-.15,.14-.2,.24-.05,.09-.07,.19-.07,.3v6.66c0,.11,.02,.21,.07,.3,.05,.1,.11,.18,.2,.24l2,1.5c.12,.08,.26,.13,.4,.13,.16,0,.32-.06,.44-.17l.02-.02,3.5-3.17,.458,.494c-.055,.281-.088,.57-.088,.866,0,2.481,2.019,4.5,4.5,4.5,1.717,0,3.194-.977,3.952-2.394l.308-.256c.08-.06,.14-.14,.18-.23,.04-.09,.06-.19,.06-.29V3.17c0-.1-.02-.2-.06-.29Zm-4.44,4.12c-.656,0-1.275,.149-1.838,.402l2.338-2.112v1.761c-.166-.019-.329-.051-.5-.051Zm.5-5.48V3.94l-3.67,3.32-.07,.07-.89-.8L12,1.52ZM2,5.12l2.66,2.88-2.66,2.88V5.12Zm.65,7.28l-.41-.31,.49-.53,2.61-2.82,.61,.67-3.3,2.99Zm4.73-2.93l-.01-.01-.67-.73-.68-.73-.63-.68-.05-.06-2.61-2.82-.49-.53,.4-.31,3.31,2.99,.01,.01,.74,.66,.81,.74,.618,.552c-.265,.303-.491,.638-.668,1.005l-.079-.087Zm.62,2.03c0-1.93,1.57-3.5,3.5-3.5s3.5,1.57,3.5,3.5-1.57,3.5-3.5,3.5-3.5-1.57-3.5-3.5Zm5-4.224V1.65l.79,.66,.21,.17,.57,.48,.43,.36v5.386c-.518-.648-1.204-1.146-2-1.43Z"
                fill="currentColor"
            />
            <polygon
                fillRule="evenodd"
                points="11.875 12.845 11.875 8.875 11.125 8.875 11.125 12.845 10.078 11.797 9.547 12.328 11.5 14.28 13.453 12.328 12.922 11.797 11.875 12.845"
            />
        </svg>
    );
}

export function FluentSvgIcon(props: FluentSvgIconProps) {
    const { iconName, ...svgProps } = props;

    switch (iconName) {
        case 'Bullseye':
            return <BullseyeIcon {...svgProps} />;

        case 'Calendar':
            return <CalendarIcon {...svgProps} />;

        case 'Checkmark':
            return <CheckmarkIcon {...svgProps} />;

        case 'ChevronDownMed':
            return <ChevronDownMedIcon {...svgProps} />;

        case 'ChevronRightMed':
            return <ChevronRightMedIcon {...svgProps} />;

        case 'ChevronUpMed':
            return <ChevronUpMedIcon {...svgProps} />;

        case 'ChromeClose':
            return <ChromeCloseIcon {...svgProps} />;

        case 'Down':
            return <DownIcon {...svgProps} />;

        case 'Feedback':
            return <FeedbackIcon {...svgProps} />;

        case 'Filter':
            return <FilterIcon {...svgProps} />;

        case 'GitHubCodespaces':
            return <GitHubCodespacesIcon {...svgProps} />;

        case 'IdentitySignIn':
            return <IdentitySignInIcon {...svgProps} />;

        case 'Intellicode':
            return <IntellicodeIcon {...svgProps} />;

        case 'Licensing':
            return <LicensingIcon {...svgProps} />;

        case 'LiveShare':
            return <LiveShareIcon {...svgProps} />;

        case 'Marketplace':
            return <MarketplaceIcon {...svgProps} />;

        case 'NuGet':
            return <NuGetIcon {...svgProps} />;

        case 'PageListFilter':
            return <PageListFilterIcon {...svgProps} />;

        case 'ReceiptCheck':
            return <ReceiptCheckIcon {...svgProps} />;

        case 'ReceiptUndelivered':
            return <ReceiptUndeliveredIcon {...svgProps} />;

        case 'RemoveFromShoppingList':
            return <RemoveFromShoppingListIcon {...svgProps} />;

        case 'Roaming':
            return <RoamingIcon {...svgProps} />;

        case 'Setup':
            return <VisualStudioSetupIcon {...svgProps} />;

        case 'SortDown':
            return <SortDownIcon {...svgProps} />;

        case 'SortUp':
            return <SortUpIcon {...svgProps} />;

        case 'Up':
            return <UpIcon {...svgProps} />;

        case 'VisualStudioCopilot':
            return <VisualStudioCopilot {...svgProps} />;

        default:
            console.warn(`Unknown icon name ${iconName}; rendering nothing.`);
            return <noscript />;
    }
}

export function SpeedHighIcon(props: SvgProps) {
    const id = useId('speed-high');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--speed-high'
        : `fluent-svg-icon fluent-svg-icon--speed-high ${className}`;

    if (height === undefined) {
        height = 19;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 19 19';
    }

    if (width === undefined) {
        width = 19;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M9.375 0C10.2344 0 11.0612 0.113932 11.8555 0.341797C12.6562 0.563151 13.4049 0.878906 14.1016 1.28906C14.7982 1.69271 15.4297 2.18099 15.9961 2.75391C16.569 3.32031 17.0573 3.95182 17.4609 4.64844C17.8711 5.34505 18.1868 6.09375 18.4082 6.89453C18.6361 7.6888 18.75 8.51562 18.75 9.375C18.75 10.2344 18.6361 11.0645 18.4082 11.8652C18.1868 12.6595 17.8711 13.4049 17.4609 14.1016C17.0573 14.7982 16.569 15.4329 15.9961 16.0059C15.4297 16.5723 14.7982 17.0605 14.1016 17.4707C13.4049 17.8743 12.6562 18.1901 11.8555 18.418C11.0612 18.6393 10.2344 18.75 9.375 18.75C8.51562 18.75 7.68555 18.6393 6.88477 18.418C6.09049 18.1901 5.34505 17.8743 4.64844 17.4707C3.95182 17.0605 3.31706 16.5723 2.74414 16.0059C2.17773 15.4329 1.68945 14.7982 1.2793 14.1016C0.875651 13.4049 0.559896 12.6595 0.332031 11.8652C0.110677 11.0645 0 10.2344 0 9.375C0 8.51562 0.110677 7.6888 0.332031 6.89453C0.559896 6.09375 0.875651 5.34505 1.2793 4.64844C1.68945 3.95182 2.17773 3.32031 2.74414 2.75391C3.31706 2.18099 3.95182 1.69271 4.64844 1.28906C5.34505 0.878906 6.09049 0.563151 6.88477 0.341797C7.68555 0.113932 8.51562 0 9.375 0ZM9.375 17.5C10.1237 17.5 10.8431 17.4023 11.5332 17.207C12.2233 17.0117 12.8678 16.7383 13.4668 16.3867C14.0723 16.0352 14.6224 15.612 15.1172 15.1172C15.612 14.6224 16.0352 14.0755 16.3867 13.4766C16.7383 12.8711 17.0117 12.2233 17.207 11.5332C17.4023 10.8431 17.5 10.1237 17.5 9.375C17.5 8.6263 17.4023 7.9069 17.207 7.2168C17.0117 6.52669 16.7383 5.88216 16.3867 5.2832C16.0352 4.67773 15.612 4.1276 15.1172 3.63281C14.6224 3.13802 14.0723 2.71484 13.4668 2.36328C12.8678 2.01172 12.2233 1.73828 11.5332 1.54297C10.8431 1.34766 10.1237 1.25 9.375 1.25C8.6263 1.25 7.9069 1.34766 7.2168 1.54297C6.52669 1.73828 5.87891 2.01172 5.27344 2.36328C4.67448 2.71484 4.1276 3.13802 3.63281 3.63281C3.13802 4.1276 2.71484 4.67773 2.36328 5.2832C2.01172 5.88216 1.73828 6.52669 1.54297 7.2168C1.34766 7.9069 1.25 8.6263 1.25 9.375C1.25 10.1237 1.34766 10.8431 1.54297 11.5332C1.73828 12.2233 2.01172 12.8711 2.36328 13.4766C2.71484 14.0755 3.13802 14.6224 3.63281 15.1172C4.1276 15.612 4.67448 16.0352 5.27344 16.3867C5.87891 16.7383 6.52669 17.0117 7.2168 17.207C7.9069 17.4023 8.6263 17.5 9.375 17.5ZM11.0547 8.55469C11.1849 8.8151 11.25 9.08854 11.25 9.375C11.25 9.63542 11.2012 9.87956 11.1035 10.1074C11.0059 10.3353 10.8724 10.5339 10.7031 10.7031C10.5339 10.8724 10.3353 11.0059 10.1074 11.1035C9.87956 11.2012 9.63542 11.25 9.375 11.25C9.11458 11.25 8.87044 11.2012 8.64258 11.1035C8.41471 11.0059 8.21615 10.8724 8.04688 10.7031C7.8776 10.5339 7.74414 10.3353 7.64648 10.1074C7.54883 9.87956 7.5 9.63542 7.5 9.375C7.5 9.11458 7.54883 8.87044 7.64648 8.64258C7.74414 8.41471 7.8776 8.21615 8.04688 8.04688C8.21615 7.8776 8.41471 7.74414 8.64258 7.64648C8.87044 7.54883 9.11458 7.5 9.375 7.5C9.66146 7.5 9.9349 7.5651 10.1953 7.69531L10.1855 7.68555L13.7793 4.0918L14.6582 4.9707L11.0645 8.56445L11.0547 8.55469ZM9.375 10C9.54427 10 9.69076 9.93815 9.81445 9.81445C9.93815 9.69076 10 9.54427 10 9.375C10 9.20573 9.93815 9.05924 9.81445 8.93555C9.69076 8.81185 9.54427 8.75 9.375 8.75C9.20573 8.75 9.05924 8.81185 8.93555 8.93555C8.81185 9.05924 8.75 9.20573 8.75 9.375C8.75 9.54427 8.81185 9.69076 8.93555 9.81445C9.05924 9.93815 9.20573 10 9.375 10ZM15.625 6.5625C15.8268 7.00521 15.9766 7.46419 16.0742 7.93945C16.1784 8.4082 16.2305 8.88672 16.2305 9.375C16.2305 10.2865 16.0579 11.1654 15.7129 12.0117C15.3743 12.8516 14.8828 13.5938 14.2383 14.2383L13.3496 13.3496C13.877 12.8223 14.2839 12.2201 14.5703 11.543C14.8568 10.8659 15 10.153 15 9.4043C15 8.59701 14.8307 7.81901 14.4922 7.07031L15.625 6.5625ZM11.6797 4.25781C10.931 3.91927 10.153 3.75 9.3457 3.75C8.83138 3.75 8.33659 3.81836 7.86133 3.95508C7.38607 4.08529 6.9401 4.27409 6.52344 4.52148C6.10677 4.76888 5.72917 5.0651 5.39062 5.41016C5.05208 5.7487 4.75911 6.1263 4.51172 6.54297C4.27083 6.95964 4.08203 7.40885 3.94531 7.89062C3.8151 8.36589 3.75 8.86068 3.75 9.375C3.75 10.1237 3.89323 10.8431 4.17969 11.5332C4.46615 12.2168 4.87305 12.8223 5.40039 13.3496L4.51172 14.2383C3.86068 13.5872 3.36263 12.8451 3.01758 12.0117C2.67253 11.1719 2.5 10.293 2.5 9.375C2.5 8.74349 2.58138 8.13477 2.74414 7.54883C2.9069 6.96289 3.13802 6.41602 3.4375 5.9082C3.73698 5.40039 4.09505 4.93815 4.51172 4.52148C4.92839 4.10482 5.39062 3.75 5.89844 3.45703C6.40625 3.15755 6.95312 2.92643 7.53906 2.76367C8.125 2.60091 8.73698 2.51953 9.375 2.51953C9.86328 2.51953 10.3418 2.57161 10.8105 2.67578C11.2858 2.77344 11.7448 2.92318 12.1875 3.125L11.6797 4.25781Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function OfflineOneDriveParachuteIcon(props: SvgProps) {
    const id = useId('offline-onedrive-parachute');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--offline-onedrive-parachute'
        : `fluent-svg-icon fluent-svg-icon--offline-onedrive-parachute ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M7.5 11C7.84375 11 8.16667 11.0677 8.46875 11.2031C8.77083 11.3333 9.03385 11.513 9.25781 11.7422C9.48698 11.9661 9.66667 12.2292 9.79688 12.5312C9.93229 12.8333 10 13.1562 10 13.5C10 13.8438 9.93229 14.1693 9.79688 14.4766C9.66667 14.7786 9.48698 15.0443 9.25781 15.2734C9.03385 15.4974 8.77083 15.6745 8.46875 15.8047C8.16667 15.9349 7.84375 16 7.5 16C7.15625 16 6.83073 15.9349 6.52344 15.8047C6.22135 15.6745 5.95573 15.4974 5.72656 15.2734C5.5026 15.0443 5.32552 14.7786 5.19531 14.4766C5.0651 14.1693 5 13.8438 5 13.5C5 13.1562 5.0651 12.8333 5.19531 12.5312C5.32552 12.2292 5.5026 11.9661 5.72656 11.7422C5.95573 11.513 6.22135 11.3333 6.52344 11.2031C6.83073 11.0677 7.15625 11 7.5 11ZM7.5 15C7.70833 15 7.90365 14.9609 8.08594 14.8828C8.26823 14.8047 8.42708 14.6979 8.5625 14.5625C8.69792 14.4271 8.80469 14.2682 8.88281 14.0859C8.96094 13.9036 9 13.7083 9 13.5C9 13.2917 8.96094 13.0964 8.88281 12.9141C8.80469 12.7318 8.69792 12.5729 8.5625 12.4375C8.42708 12.3021 8.26823 12.1953 8.08594 12.1172C7.90365 12.0391 7.70833 12 7.5 12C7.29167 12 7.09635 12.0391 6.91406 12.1172C6.73177 12.1953 6.57292 12.3021 6.4375 12.4375C6.30208 12.5729 6.19531 12.7318 6.11719 12.9141C6.03906 13.0964 6 13.2917 6 13.5C6 13.7083 6.03906 13.9036 6.11719 14.0859C6.19531 14.2682 6.30208 14.4271 6.4375 14.5625C6.57292 14.6979 6.73177 14.8047 6.91406 14.8828C7.09635 14.9609 7.29167 15 7.5 15ZM7.5 0C8.1875 0 8.84896 0.0911458 9.48438 0.273438C10.125 0.450521 10.724 0.703125 11.2812 1.03125C11.8385 1.35417 12.3438 1.74479 12.7969 2.20312C13.2552 2.65625 13.6458 3.16146 13.9688 3.71875C14.2969 4.27604 14.5495 4.875 14.7266 5.51562C14.9089 6.15104 15 6.8125 15 7.5V7.74219L10.3438 11.4688C10.2448 11.3333 10.138 11.2057 10.0234 11.0859C9.90885 10.9609 9.78385 10.8464 9.64844 10.7422L13.9297 7.3125C13.4609 7.13021 12.9792 6.99219 12.4844 6.89844C11.9948 6.79948 11.4974 6.75 10.9922 6.75C10.4766 6.75 9.96615 6.79948 9.46094 6.89844C8.96094 6.9974 8.47396 7.14323 8 7.33594V10.0391C7.91667 10.0286 7.83333 10.0208 7.75 10.0156C7.66667 10.0052 7.58333 10 7.5 10C7.41667 10 7.33333 10.0052 7.25 10.0156C7.16667 10.0208 7.08333 10.0286 7 10.0391V7.34375C6.03646 6.94792 5.03385 6.75 3.99219 6.75C3.48698 6.75 2.98958 6.79948 2.5 6.89844C2.01562 6.99219 1.53906 7.13021 1.07031 7.3125L5.35156 10.7422C5.21615 10.8464 5.09115 10.9609 4.97656 11.0859C4.86198 11.2057 4.75521 11.3333 4.65625 11.4688L0 7.74219V7.5C0 6.8125 0.0885417 6.15104 0.265625 5.51562C0.447917 4.875 0.700521 4.27604 1.02344 3.71875C1.35156 3.16146 1.74219 2.65625 2.19531 2.20312C2.65365 1.74479 3.16146 1.35417 3.71875 1.03125C4.27604 0.703125 4.8724 0.450521 5.50781 0.273438C6.14844 0.0911458 6.8125 0 7.5 0ZM7.5625 6.4375C8.11979 6.21875 8.67708 6.04948 9.23438 5.92969C9.79688 5.8099 10.3776 5.75 10.9766 5.75C11.9661 5.75 12.9297 5.91146 13.8672 6.23438C13.7161 5.47917 13.4453 4.78125 13.0547 4.14062C12.6641 3.5 12.1849 2.94792 11.6172 2.48438C11.0547 2.01562 10.4193 1.65104 9.71094 1.39062C9.00781 1.13021 8.27083 1 7.5 1C6.72396 1 5.98438 1.13021 5.28125 1.39062C4.57812 1.65104 3.94271 2.01302 3.375 2.47656C2.8125 2.9401 2.33594 3.49219 1.94531 4.13281C1.55469 4.77344 1.28385 5.47135 1.13281 6.22656C2.07552 5.90885 3.03646 5.75 4.01562 5.75C4.30208 5.75 4.59375 5.76562 4.89062 5.79688C5.19271 5.82292 5.49219 5.86719 5.78906 5.92969C6.09115 5.98698 6.38542 6.0599 6.67188 6.14844C6.96354 6.23698 7.23958 6.34115 7.5 6.46094L7.5625 6.4375Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ChatIcon(props: SvgProps) {
    const id = useId('chat');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--chat'
        : `fluent-svg-icon fluent-svg-icon--chat ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path d="M0 0H16V11H5.71094L2 14.7109V11H0V0ZM15 10V1H1V10H3V12.2891L5.28906 10H15Z" fill="currentColor" />
        </svg>
    );
}

export function DoubleColumnEditIcon(props: SvgProps) {
    const id = useId('double-column-edit');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--double-column-edit'
        : `fluent-svg-icon fluent-svg-icon--double-column-edit ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M9 8.01562L8 9.02344V0H15V3.82812C14.9062 3.8125 14.8125 3.79948 14.7188 3.78906C14.625 3.77865 14.5312 3.77344 14.4375 3.77344C14.3646 3.77344 14.2917 3.77865 14.2188 3.78906C14.1458 3.79427 14.0729 3.80208 14 3.8125V1H9V8.01562ZM7 10.0234L6.60938 10.4219L6.21094 12H0V0H7V10.0234ZM6 1H1V11H6V1ZM14.4375 5C14.6562 5 14.8594 5.03906 15.0469 5.11719C15.2396 5.19531 15.4062 5.30469 15.5469 5.44531C15.6875 5.58594 15.7969 5.75 15.875 5.9375C15.9583 6.125 16 6.32812 16 6.54688C16 6.75 15.9609 6.94792 15.8828 7.14062C15.8047 7.33333 15.6927 7.5026 15.5469 7.64844L9.94531 13.2656L7 14L7.73438 11.0547L13.3359 5.44531C13.4818 5.29948 13.651 5.1901 13.8438 5.11719C14.0365 5.03906 14.2344 5 14.4375 5ZM14.8359 6.94531C14.9453 6.83594 15 6.70312 15 6.54688C15 6.38542 14.9453 6.25521 14.8359 6.15625C14.7318 6.05208 14.599 6 14.4375 6C14.3646 6 14.2943 6.01302 14.2266 6.03906C14.1589 6.0599 14.099 6.09635 14.0469 6.14844L8.64062 11.5703L8.375 12.625L9.42969 12.3594L14.8359 6.94531Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function CircleCheckIcon(props: SvgProps) {
    const id = useId('circle-check');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--circle-check'
        : `fluent-svg-icon fluent-svg-icon--circle-check ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M12.8359 5.64062L13.6484 4.82812C13.7682 5.17708 13.8568 5.53385 13.9141 5.89844C13.9714 6.26302 14 6.63021 14 7C14 7.64583 13.9167 8.26562 13.75 8.85938C13.5833 9.45312 13.3464 10.0104 13.0391 10.5312C12.737 11.0469 12.3724 11.5182 11.9453 11.9453C11.5182 12.3724 11.0443 12.7396 10.5234 13.0469C10.0078 13.349 9.45052 13.5833 8.85156 13.75C8.25781 13.9167 7.64062 14 7 14C6.35417 14 5.73438 13.9167 5.14062 13.75C4.54688 13.5833 3.98958 13.349 3.46875 13.0469C2.95312 12.7396 2.48177 12.3724 2.05469 11.9453C1.6276 11.5182 1.26042 11.0469 0.953125 10.5312C0.651042 10.0104 0.416667 9.45312 0.25 8.85938C0.0833333 8.26042 0 7.64062 0 7C0 6.35938 0.0833333 5.74219 0.25 5.14844C0.416667 4.54948 0.651042 3.99219 0.953125 3.47656C1.26042 2.95573 1.6276 2.48177 2.05469 2.05469C2.48177 1.6276 2.95312 1.26302 3.46875 0.960938C3.98958 0.653646 4.54688 0.416667 5.14062 0.25C5.73958 0.0833333 6.35938 0 7 0C7.875 0 8.71354 0.158854 9.51562 0.476562C10.3177 0.789062 11.0443 1.23958 11.6953 1.82812L10.9922 2.53125C10.4401 2.04167 9.82292 1.66406 9.14062 1.39844C8.45833 1.13281 7.74479 1 7 1C6.44792 1 5.91667 1.07292 5.40625 1.21875C4.89583 1.35938 4.41667 1.5599 3.96875 1.82031C3.52604 2.08073 3.1224 2.39323 2.75781 2.75781C2.39323 3.1224 2.08073 3.52865 1.82031 3.97656C1.5599 4.41927 1.35677 4.89583 1.21094 5.40625C1.07031 5.91667 1 6.44792 1 7C1 7.55208 1.07031 8.08333 1.21094 8.59375C1.35677 9.10417 1.5599 9.58333 1.82031 10.0312C2.08073 10.474 2.39323 10.8776 2.75781 11.2422C3.1224 11.6068 3.52604 11.9193 3.96875 12.1797C4.41667 12.4401 4.89583 12.6432 5.40625 12.7891C5.91667 12.9297 6.44792 13 7 13C7.55208 13 8.08333 12.9297 8.59375 12.7891C9.10417 12.6432 9.58073 12.4401 10.0234 12.1797C10.4714 11.9193 10.8776 11.6068 11.2422 11.2422C11.6068 10.8776 11.9193 10.474 12.1797 10.0312C12.4401 9.58333 12.6406 9.10417 12.7812 8.59375C12.9271 8.08333 13 7.55208 13 7C13 6.76562 12.9844 6.53646 12.9531 6.3125C12.9271 6.08854 12.888 5.86458 12.8359 5.64062ZM3.85156 6.14844L6.5 8.79688L14.1484 1.14844L14.8516 1.85156L6.5 10.2031L3.14844 6.85156L3.85156 6.14844Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function CalculatorAdditionIcon(props: SvgProps) {
    const id = useId('calculator-addition');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--calculator-addition'
        : `fluent-svg-icon fluent-svg-icon--calculator-addition ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path d="M16 7.5V8.5H8.5V16H7.5V8.5H0V7.5H7.5V0H8.5V7.5H16Z" fill="currentColor" />
        </svg>
    );
}

export function ShareIcon(props: SvgProps) {
    const id = useId('share');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--share'
        : `fluent-svg-icon fluent-svg-icon--share ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M15.6016 7.5L0.140625 14.3984L1.85938 7.5L0.140625 0.601562L15.6016 7.5ZM1.60938 2.35156L2.77344 7.02344H12.0703L1.60938 2.35156ZM2.75781 8.02344L1.60938 12.6484L11.9766 8.02344H2.75781Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ClockIcon(props: SvgProps) {
    const id = useId('clock');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--clock'
        : `fluent-svg-icon fluent-svg-icon--clock ${className}`;

    if (height === undefined) {
        height = 13;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 14 13';
    }

    if (width === undefined) {
        width = 14;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M7 13C6.4349 13 5.8903 12.9271 5.36621 12.7812C4.84668 12.6354 4.35905 12.4303 3.90332 12.166C3.45215 11.8971 3.03971 11.5781 2.66602 11.209C2.29688 10.8353 1.97786 10.4229 1.70898 9.97168C1.44466 9.51595 1.23958 9.02832 1.09375 8.50879C0.947917 7.9847 0.875 7.4401 0.875 6.875C0.875 6.3099 0.947917 5.76758 1.09375 5.24805C1.23958 4.72396 1.44466 4.23633 1.70898 3.78516C1.97786 3.32943 2.29688 2.91699 2.66602 2.54785C3.03971 2.17415 3.45215 1.85514 3.90332 1.59082C4.35905 1.32194 4.84668 1.11458 5.36621 0.96875C5.8903 0.822917 6.4349 0.75 7 0.75C7.5651 0.75 8.10742 0.822917 8.62695 0.96875C9.15104 1.11458 9.63867 1.32194 10.0898 1.59082C10.5456 1.85514 10.958 2.17415 11.3271 2.54785C11.7008 2.91699 12.0199 3.32943 12.2842 3.78516C12.5531 4.23633 12.7604 4.72396 12.9062 5.24805C13.0521 5.76758 13.125 6.3099 13.125 6.875C13.125 7.4401 13.0521 7.9847 12.9062 8.50879C12.7604 9.02832 12.5531 9.51595 12.2842 9.97168C12.0199 10.4229 11.7008 10.8353 11.3271 11.209C10.958 11.5781 10.5456 11.8971 10.0898 12.166C9.63867 12.4303 9.15104 12.6354 8.62695 12.7812C8.10742 12.9271 7.5651 13 7 13ZM7 1.625C6.27539 1.625 5.59408 1.764 4.95605 2.04199C4.32259 2.31543 3.7666 2.69141 3.28809 3.16992C2.81413 3.64388 2.43815 4.19987 2.16016 4.83789C1.88672 5.47135 1.75 6.15039 1.75 6.875C1.75 7.59961 1.88672 8.28092 2.16016 8.91895C2.43815 9.55241 2.81413 10.1084 3.28809 10.5869C3.7666 11.0609 4.32259 11.4368 4.95605 11.7148C5.59408 11.9883 6.27539 12.125 7 12.125C7.72461 12.125 8.40365 11.9883 9.03711 11.7148C9.67513 11.4368 10.2311 11.0609 10.7051 10.5869C11.1836 10.1084 11.5596 9.55241 11.833 8.91895C12.111 8.28092 12.25 7.59961 12.25 6.875C12.25 6.15039 12.111 5.47135 11.833 4.83789C11.5596 4.19987 11.1836 3.64388 10.7051 3.16992C10.2311 2.69141 9.67513 2.31543 9.03711 2.04199C8.40365 1.764 7.72461 1.625 7 1.625ZM7 6.875V3.375H6.125V7.75H9.625V6.875H7Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function EditIcon(props: SvgProps) {
    const id = useId('edit');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--edit'
        : `fluent-svg-icon fluent-svg-icon--edit ${className}`;

    if (height === undefined) {
        height = 14;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 14 14';
    }

    if (width === undefined) {
        width = 14;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            width={width}
            height={height}
            className={iconClassName}
            viewBox="0 0 14 14"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M14 2.29004C14 2.59082 13.943 2.88249 13.8291 3.16504C13.7152 3.44759 13.5488 3.69824 13.3301 3.91699L4.32715 12.9199L0 14L1.08008 9.67285L10.083 0.669922C10.3018 0.451172 10.5524 0.284831 10.835 0.170898C11.1175 0.0569661 11.4092 0 11.71 0C12.0244 0 12.3206 0.0615234 12.5986 0.18457C12.8766 0.30306 13.1182 0.467122 13.3232 0.676758C13.5329 0.881836 13.6969 1.12337 13.8154 1.40137C13.9385 1.67936 14 1.97559 14 2.29004ZM2.22852 9.76172C2.71159 9.92122 3.1263 10.1764 3.47266 10.5273C3.82357 10.8737 4.07878 11.2884 4.23828 11.7715L11.6279 4.375L9.625 2.37207L2.22852 9.76172ZM1.20312 12.7969L3.45215 12.2363C3.40658 12.0267 3.33138 11.8285 3.22656 11.6416C3.1263 11.4548 3.00326 11.2884 2.85742 11.1426C2.71159 10.9967 2.54525 10.8737 2.3584 10.7734C2.17155 10.6686 1.97331 10.5934 1.76367 10.5479L1.20312 12.7969ZM12.25 3.75293C12.3639 3.639 12.4733 3.5319 12.5781 3.43164C12.6829 3.33138 12.7764 3.22656 12.8584 3.11719C12.9404 3.00326 13.0042 2.88249 13.0498 2.75488C13.0999 2.62272 13.125 2.47005 13.125 2.29688C13.125 2.10091 13.0863 1.91862 13.0088 1.75C12.9359 1.57682 12.8333 1.42643 12.7012 1.29883C12.5736 1.16667 12.4232 1.06413 12.25 0.991211C12.0814 0.913737 11.8991 0.875 11.7031 0.875C11.5299 0.875 11.3773 0.900065 11.2451 0.950195C11.1175 0.995768 10.9967 1.05957 10.8828 1.1416C10.7734 1.22363 10.6686 1.31706 10.5684 1.42188C10.4681 1.52669 10.361 1.63607 10.2471 1.75L12.25 3.75293Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function DotIcon(props: SvgProps) {
    const id = useId('dot');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--dot'
        : `fluent-svg-icon fluent-svg-icon--dot ${className}`;

    if (height === undefined) {
        height = 11;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 11 11';
    }

    if (width === undefined) {
        width = 11;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            className={iconClassName}
            id={id}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <circle cx="5.5" cy="5.5" r="5.5" fill="currentColor" />
        </svg>
    );
}

export function ReceiptCheckIcon(props: SvgProps) {
    const id = useId('receipt-check');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--receipt-check'
        : `fluent-svg-icon fluent-svg-icon--receipt-check ${className}`;

    if (height === undefined) {
        height = 11;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 11';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M5.5 0C6.00521 0 6.49219 0.0651042 6.96094 0.195312C7.42969 0.325521 7.86719 0.510417 8.27344 0.75C8.6849 0.989583 9.05729 1.27604 9.39062 1.60938C9.72396 1.94271 10.0104 2.3151 10.25 2.72656C10.4896 3.13281 10.6745 3.57031 10.8047 4.03906C10.9349 4.50781 11 4.99479 11 5.5C11 6.00521 10.9349 6.49219 10.8047 6.96094C10.6745 7.42969 10.4896 7.86979 10.25 8.28125C10.0104 8.6875 9.72396 9.05729 9.39062 9.39062C9.05729 9.72396 8.6849 10.0104 8.27344 10.25C7.86719 10.4896 7.42969 10.6745 6.96094 10.8047C6.49219 10.9349 6.00521 11 5.5 11C4.99479 11 4.50781 10.9349 4.03906 10.8047C3.57031 10.6745 3.13021 10.4896 2.71875 10.25C2.3125 10.0104 1.94271 9.72396 1.60938 9.39062C1.27604 9.05729 0.989583 8.6875 0.75 8.28125C0.510417 7.86979 0.325521 7.42969 0.195312 6.96094C0.0651042 6.49219 0 6.00521 0 5.5C0 4.99479 0.0651042 4.50781 0.195312 4.03906C0.325521 3.57031 0.510417 3.13281 0.75 2.72656C0.989583 2.3151 1.27604 1.94271 1.60938 1.60938C1.94271 1.27604 2.3125 0.989583 2.71875 0.75C3.13021 0.510417 3.57031 0.325521 4.03906 0.195312C4.50781 0.0651042 4.99479 0 5.5 0ZM5.5 10C6.11979 10 6.70312 9.88281 7.25 9.64844C7.79688 9.40885 8.27344 9.08594 8.67969 8.67969C9.08594 8.27344 9.40625 7.79688 9.64062 7.25C9.88021 6.70312 10 6.11979 10 5.5C10 4.88021 9.88021 4.29688 9.64062 3.75C9.40625 3.20312 9.08594 2.72656 8.67969 2.32031C8.27344 1.91406 7.79688 1.59375 7.25 1.35938C6.70312 1.11979 6.11979 1 5.5 1C4.88021 1 4.29688 1.11979 3.75 1.35938C3.20312 1.59375 2.72656 1.91406 2.32031 2.32031C1.91406 2.72656 1.59115 3.20312 1.35156 3.75C1.11719 4.29688 1 4.88021 1 5.5C1 6.11979 1.11719 6.70312 1.35156 7.25C1.59115 7.79688 1.91406 8.27344 2.32031 8.67969C2.72656 9.08594 3.20312 9.40885 3.75 9.64844C4.29688 9.88281 4.88021 10 5.5 10ZM7.64844 3.64844L8.35156 4.35156L4.5 8.20312L2.64844 6.35156L3.35156 5.64844L4.5 6.79688L7.64844 3.64844ZM13 5H16V6H13V5ZM16 2V3H12V2H16ZM13 8H16V9H13V8Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ReceiptUndeliveredIcon(props: SvgProps) {
    const id = useId('receipt-undelivered');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--receipt-undelivered'
        : `fluent-svg-icon fluent-svg-icon--receipt-undelivered ${className}`;

    if (height === undefined) {
        height = 11;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 11';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M5.5 0C6.00521 0 6.49219 0.0651042 6.96094 0.195312C7.42969 0.325521 7.86719 0.510417 8.27344 0.75C8.6849 0.989583 9.05729 1.27604 9.39062 1.60938C9.72396 1.94271 10.0104 2.3151 10.25 2.72656C10.4896 3.13281 10.6745 3.57031 10.8047 4.03906C10.9349 4.50781 11 4.99479 11 5.5C11 6.00521 10.9349 6.49219 10.8047 6.96094C10.6745 7.42969 10.4896 7.86979 10.25 8.28125C10.0104 8.6875 9.72396 9.05729 9.39062 9.39062C9.05729 9.72396 8.6849 10.0104 8.27344 10.25C7.86719 10.4896 7.42969 10.6745 6.96094 10.8047C6.49219 10.9349 6.00521 11 5.5 11C4.99479 11 4.50781 10.9349 4.03906 10.8047C3.57031 10.6745 3.13021 10.4896 2.71875 10.25C2.3125 10.0104 1.94271 9.72396 1.60938 9.39062C1.27604 9.05729 0.989583 8.6875 0.75 8.28125C0.510417 7.86979 0.325521 7.42969 0.195312 6.96094C0.0651042 6.49219 0 6.00521 0 5.5C0 4.99479 0.0651042 4.50781 0.195312 4.03906C0.325521 3.57031 0.510417 3.13281 0.75 2.72656C0.989583 2.3151 1.27604 1.94271 1.60938 1.60938C1.94271 1.27604 2.3125 0.989583 2.71875 0.75C3.13021 0.510417 3.57031 0.325521 4.03906 0.195312C4.50781 0.0651042 4.99479 0 5.5 0ZM5.5 10C6.11979 10 6.70312 9.88281 7.25 9.64844C7.79688 9.40885 8.27344 9.08594 8.67969 8.67969C9.08594 8.27344 9.40625 7.79688 9.64062 7.25C9.88021 6.70312 10 6.11979 10 5.5C10 4.88021 9.88021 4.29688 9.64062 3.75C9.40625 3.20312 9.08594 2.72656 8.67969 2.32031C8.27344 1.91406 7.79688 1.59375 7.25 1.35938C6.70312 1.11979 6.11979 1 5.5 1C4.88021 1 4.29688 1.11979 3.75 1.35938C3.20312 1.59375 2.72656 1.91406 2.32031 2.32031C1.91406 2.72656 1.59115 3.20312 1.35156 3.75C1.11719 4.29688 1 4.88021 1 5.5C1 6.11979 1.11719 6.70312 1.35156 7.25C1.59115 7.79688 1.91406 8.27344 2.32031 8.67969C2.72656 9.08594 3.20312 9.40885 3.75 9.64844C4.29688 9.88281 4.88021 10 5.5 10ZM13 5H16V6H13V5ZM16 2V3H12V2H16ZM13 8H16V9H13V8ZM7.97656 3.73438L6.21094 5.5L7.97656 7.27344L7.27344 7.97656L5.5 6.21094L3.72656 7.98438L3.02344 7.27344L4.79688 5.5L3.02344 3.72656L3.72656 3.02344L5.5 4.79688L7.27344 3.02344L7.97656 3.73438Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function BullseyeIcon(props: SvgProps) {
    const id = useId('bullseye');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--bullseye'
        : `fluent-svg-icon fluent-svg-icon--bullseye ${className}`;

    if (height === undefined) {
        height = 19;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 19 19';
    }

    if (width === undefined) {
        width = 19;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M9 6.25C8.47917 6.25 7.99089 6.34766 7.53516 6.54297C7.08594 6.73828 6.6888 7.00846 6.34375 7.35352C6.00521 7.69206 5.73828 8.08919 5.54297 8.54492C5.34766 9.00065 5.25 9.48568 5.25 10C5.25 10.5208 5.34766 11.0091 5.54297 11.4648C5.73828 11.9141 6.00521 12.3112 6.34375 12.6562C6.6888 12.9948 7.08594 13.2617 7.53516 13.457C7.99089 13.6523 8.47917 13.75 9 13.75C9.51432 13.75 9.99935 13.6523 10.4551 13.457C10.9108 13.2617 11.3079 12.9948 11.6465 12.6562C11.9915 12.3112 12.2617 11.9141 12.457 11.4648C12.6523 11.0091 12.75 10.5208 12.75 10C12.75 9.58333 12.6784 9.16667 12.5352 8.75H13.8438C13.8958 8.95833 13.9349 9.16667 13.9609 9.375C13.987 9.58333 14 9.79167 14 10C14 10.6901 13.8665 11.3379 13.5996 11.9434C13.3392 12.5488 12.9811 13.0794 12.5254 13.5352C12.0762 13.9844 11.5488 14.3424 10.9434 14.6094C10.3379 14.8698 9.6901 15 9 15C8.3099 15 7.66211 14.8698 7.05664 14.6094C6.45117 14.3424 5.92057 13.9844 5.46484 13.5352C5.01562 13.0794 4.66081 12.5488 4.40039 11.9434C4.13997 11.3379 4.00977 10.6901 4.00977 10C4.00977 9.3099 4.13997 8.66211 4.40039 8.05664C4.66081 7.45117 5.01562 6.92383 5.46484 6.47461C5.92057 6.01888 6.45117 5.66081 7.05664 5.40039C7.66211 5.13346 8.3099 5 9 5C9.20833 5 9.41667 5.01302 9.625 5.03906C9.83333 5.0651 10.0417 5.10417 10.25 5.15625V6.46484C9.83333 6.32161 9.41667 6.25 9 6.25ZM17.457 7.76367C17.6523 8.49935 17.75 9.24479 17.75 10C17.75 10.8008 17.6458 11.5755 17.4375 12.3242C17.2292 13.0664 16.9329 13.763 16.5488 14.4141C16.1712 15.0586 15.7155 15.6478 15.1816 16.1816C14.6478 16.7155 14.0553 17.1745 13.4043 17.5586C12.7598 17.9362 12.0632 18.2292 11.3145 18.4375C10.5723 18.6458 9.80078 18.75 9 18.75C8.19922 18.75 7.42448 18.6458 6.67578 18.4375C5.93359 18.2292 5.23698 17.9362 4.58594 17.5586C3.94141 17.1745 3.35221 16.7155 2.81836 16.1816C2.28451 15.6478 1.82552 15.0586 1.44141 14.4141C1.0638 13.763 0.770833 13.0664 0.5625 12.3242C0.354167 11.582 0.25 10.8073 0.25 10C0.25 9.19922 0.354167 8.42773 0.5625 7.68555C0.770833 6.93685 1.0638 6.24023 1.44141 5.5957C1.82552 4.94466 2.28451 4.35221 2.81836 3.81836C3.35221 3.28451 3.94141 2.82878 4.58594 2.45117C5.23698 2.06706 5.93359 1.77083 6.67578 1.5625C7.41797 1.35417 8.19271 1.25 9 1.25C9.75521 1.25 10.5007 1.34766 11.2363 1.54297L10.25 2.5293V2.60742C10.0417 2.57487 9.83333 2.54883 9.625 2.5293C9.41667 2.50977 9.20833 2.5 9 2.5C8.3099 2.5 7.64583 2.59115 7.00781 2.77344C6.36979 2.94922 5.77083 3.19987 5.21094 3.52539C4.65755 3.85091 4.15299 4.24154 3.69727 4.69727C3.24154 5.15299 2.85091 5.66081 2.52539 6.2207C2.19987 6.77409 1.94596 7.36979 1.76367 8.00781C1.58789 8.64583 1.5 9.3099 1.5 10C1.5 10.6901 1.58789 11.3542 1.76367 11.9922C1.94596 12.6302 2.19987 13.2292 2.52539 13.7891C2.85091 14.3424 3.24154 14.847 3.69727 15.3027C4.15299 15.7585 4.65755 16.1491 5.21094 16.4746C5.77083 16.8001 6.36979 17.054 7.00781 17.2363C7.64583 17.4121 8.3099 17.5 9 17.5C9.6901 17.5 10.3542 17.4121 10.9922 17.2363C11.6302 17.054 12.2259 16.8001 12.7793 16.4746C13.3392 16.1491 13.847 15.7585 14.3027 15.3027C14.7585 14.847 15.1491 14.3424 15.4746 13.7891C15.8001 13.2292 16.0508 12.6302 16.2266 11.9922C16.4089 11.3542 16.5 10.6901 16.5 10C16.5 9.79167 16.4902 9.58333 16.4707 9.375C16.4512 9.16667 16.4251 8.95833 16.3926 8.75H16.4707L17.457 7.76367ZM11.5 6.61133V3.80859L15.25 0.0585938V3.75H18.9414L15.1914 7.5H12.3887L10.2109 9.67773C10.237 9.77539 10.25 9.88281 10.25 10C10.25 10.1758 10.2174 10.3385 10.1523 10.4883C10.0872 10.638 9.99609 10.7715 9.87891 10.8887C9.76823 10.9993 9.63802 11.0872 9.48828 11.1523C9.33854 11.2174 9.17578 11.25 9 11.25C8.82422 11.25 8.66146 11.2174 8.51172 11.1523C8.36198 11.0872 8.22852 10.9993 8.11133 10.8887C8.00065 10.7715 7.91276 10.638 7.84766 10.4883C7.78255 10.3385 7.75 10.1758 7.75 10C7.75 9.82422 7.78255 9.66146 7.84766 9.51172C7.91276 9.36198 8.00065 9.23177 8.11133 9.12109C8.22852 9.00391 8.36198 8.91276 8.51172 8.84766C8.66146 8.78255 8.82422 8.75 9 8.75C9.11719 8.75 9.22461 8.76302 9.32227 8.78906L11.5 6.61133ZM12.75 6.25H14.6738L15.9238 5H14V3.07617L12.75 4.32617V6.25Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function RemoveFromShoppingListIcon(props: SvgProps) {
    const id = useId('remove-from-shopping-list');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--remove-from-shopping-list'
        : `fluent-svg-icon fluent-svg-icon--remove-from-shopping-list ${className}`;

    if (height === undefined) {
        height = 20;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 18 20';
    }

    if (width === undefined) {
        width = 18;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M4.25 7.5V8.75H3V7.5H4.25ZM13 7.5V8.75H5.5V7.5H13ZM3 12.5V11.25H4.25V12.5H3ZM5.5 12.5V11.25H13V12.5H5.5ZM4.25 3.75V5H3V3.75H4.25ZM13 3.75V5H5.5V3.75H13ZM1.75 1.25V16.25H10.5V17.5H0.5V0H15.5V12.5H14.25V1.25H1.75ZM17.8145 14.8145L15.7539 16.875L17.8145 18.9355L16.9355 19.8145L14.875 17.7539L12.8145 19.8145L11.9355 18.9355L13.9961 16.875L11.9355 14.8145L12.8145 13.9355L14.875 15.9961L16.9355 13.9355L17.8145 14.8145Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ChromeCloseIcon(props: SvgProps) {
    const id = useId('chrome-close');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--chrome-close'
        : `fluent-svg-icon fluent-svg-icon--chrome-close ${className}`;

    if (height === undefined) {
        height = 12;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 12 12';
    }

    if (width === undefined) {
        width = 12;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M6.84961 6L12 11.1504L11.1504 12L6 6.84961L0.849609 12L0 11.1504L5.15039 6L0 0.849609L0.849609 0L6 5.15039L11.1504 0L12 0.849609L6.84961 6Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function CheckmarkIcon(props: SvgProps) {
    const id = useId('checkmark');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--checkmark'
        : `fluent-svg-icon fluent-svg-icon--checkmark ${className}`;

    if (height === undefined) {
        height = 12;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 17 12';
    }

    if (width === undefined) {
        width = 17;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M16.0763 0.912992L5.55719 11.4419L0.0762634 5.9511L0.961872 5.06549L5.55719 9.65097L15.1906 0.0273438L16.0763 0.912992Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function CalendarIcon(props: SvgProps) {
    const id = useId('calendar');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--calendar'
        : `fluent-svg-icon fluent-svg-icon--calendar ${className}`;

    if (height === undefined) {
        height = 12;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 12 12';
    }

    if (width === undefined) {
        width = 12;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M4.5 4.5H5.25V5.25H4.5V4.5ZM6.75 9H7.5V9.75H6.75V9ZM9 4.5H9.75V5.25H9V4.5ZM6.75 4.5H7.5V5.25H6.75V4.5ZM4.5 6H5.25V6.75H4.5V6ZM2.25 6H3V6.75H2.25V6ZM9 6H9.75V6.75H9V6ZM6.75 6H7.5V6.75H6.75V6ZM4.5 7.5H5.25V8.25H4.5V7.5ZM2.25 7.5H3V8.25H2.25V7.5ZM9 7.5H9.75V8.25H9V7.5ZM6.75 7.5H7.5V8.25H6.75V7.5ZM4.5 9H5.25V9.75H4.5V9ZM2.25 9H3V9.75H2.25V9ZM12 0.75V11.25H0V0.75H2.25V0H3V0.75H9V0H9.75V0.75H12ZM0.75 1.5V3H11.25V1.5H9.75V2.25H9V1.5H3V2.25H2.25V1.5H0.75ZM11.25 10.5V3.75H0.75V10.5H11.25Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function SortDownIcon(props: SvgProps) {
    const id = useId('sort-down');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--sort-down'
        : `fluent-svg-icon fluent-svg-icon--sort-down ${className}`;

    if (height === undefined) {
        height = 17;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 9 17';
    }

    if (width === undefined) {
        width = 9;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M7.70332 12.3783L8.41739 13.0846L4.54883 16.9531L0.672424 13.0846L1.3865 12.3783L4.03878 15.0306L4.03093 0.953125H5.03534L5.04319 15.0384L7.70332 12.3783Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function SortUpIcon(props: SvgProps) {
    const id = useId('sort-up');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--sort-up'
        : `fluent-svg-icon fluent-svg-icon--sort-up ${className}`;

    if (height === undefined) {
        height = 12;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 7 12';
    }

    if (width === undefined) {
        width = 7;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M6.51367 2.92383L5.98633 3.45117L4.00586 1.47656L4 12H3.25L3.25586 1.45898L1.26367 3.45117L0.736328 2.92383L3.625 0.0351562L6.51367 2.92383Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function FilterIcon(props: SvgProps) {
    const id = useId('filter');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--filter'
        : `fluent-svg-icon fluent-svg-icon--filter ${className}`;

    if (height === undefined) {
        height = 14;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 17 14';
    }

    if (width === undefined) {
        width = 17;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M16.0944 0V1.71094L10.0944 7.71094V14H6.09436V7.71094L0.0943604 1.71094V0H16.0944ZM15.0944 1H1.09436V1.28906L7.09436 7.28906V13H9.09436V7.28906L15.0944 1.28906V1Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function DownIcon(props: SvgProps) {
    const id = useId('down');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--down'
        : `fluent-svg-icon fluent-svg-icon--down ${className}`;

    if (height === undefined) {
        height = 17;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M15.3967 9.57999L8.02351 16.9532L0.65033 9.57999L1.35552 8.87479L7.52202 15.0413V0.953125H8.52496V15.0413L14.6915 8.87479L15.3967 9.57999Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function UpIcon(props: SvgProps) {
    const id = useId('up');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--up'
        : `fluent-svg-icon fluent-svg-icon--up ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            id={id}
            className={iconClassName}
            width={width}
            height={height}
            viewBox={viewBox}
            color="currentColor"
            fill="currentColor"
            x={x}
            y={y}
            xmlns="http://www.w3.org/2000/svg"
        >
            {title && <title>{title}</title>}
            <path
                d="M14.6915 8.07837L8.52498 1.91188V16H7.52204V1.91188L1.35554 8.07837L0.650352 7.37318L8.02353 0L15.3967 7.37318L14.6915 8.07837Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function ClearIcon(props: SvgProps) {
    const id = useId('clear');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--clear'
        : `fluent-svg-icon fluent-svg-icon--clear ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 16 16';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            className={iconClassName}
            color="currentColor"
            fill="currentColor"
            height={height}
            id={id}
            viewBox={viewBox}
            width={width}
            x={x}
            xmlns="http://www.w3.org/2000/svg"
            y={y}
        >
            {title && <title>{title}</title>}
            <path
                d="M8.8757 8.84874L16.1513 16.1323L15.4349 16.8487L8.15135 9.57309L0.867785 16.8487L0.151367 16.1323L7.427 8.84874L0.151367 1.56517L0.867785 0.848755L8.15135 8.12439L15.4349 0.848755L16.1513 1.56517L8.8757 8.84874Z"
                fill="currentColor"
            />
        </svg>
    );
}

export function NuGetIcon(props: SvgProps) {
    const id = useId('nuget');
    const { className, title } = props;
    let { height, viewBox, width, x, y } = props;

    const iconClassName = isUndefinedOrWhiteSpace(className)
        ? 'fluent-svg-icon fluent-svg-icon--nuget'
        : `fluent-svg-icon fluent-svg-icon--nuget ${className}`;

    if (height === undefined) {
        height = 16;
    }

    if (isUndefinedOrWhiteSpace(viewBox)) {
        viewBox = '0 0 24 23.35';
    }

    if (width === undefined) {
        width = 16;
    }

    if (x === undefined) {
        x = 0;
    }

    if (y === undefined) {
        y = 0;
    }

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height={height}
            width={width}
            id={id}
            version="1.1"
            viewBox={viewBox}
            className={iconClassName}
            color="currentColor"
            fill="currentColor"
            style={{ marginRight: '7px' }}
        >
            {title && <title>{title}</title>}
            <g id="mark">
                <path
                    d="M 17.87,4.34 H 11.19 A 6.16,6.16 0 0 0 5,10.51 v 6.68 a 6.16,6.16 0 0 0 6.16,6.16 h 6.68 A 6.16,6.16 0 0 0 24,17.18 V 10.51 A 6.16,6.16 0 0 0 17.87,4.34 Z m -8.16,7.8 a 2.5,2.5 0 1 1 2.5,-2.5 2.5,2.5 0 0 1 -2.5,2.51 z m 8,9.17 a 4,4 0 1 1 4,-4 4,4 0 0 1 -4.02,4.01 z"
                    data-name="mark"
                    id="mark-2"
                />
                <circle id="circle49" r="2" cy="2" cx="2" />
            </g>
        </svg>
    );
}
